import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { AddPhotoAlternateOutlined } from '@mui/icons-material';
import ThumbnailUrlDialog from './ThumbnailUrlDialog';
import useThumbnailDrop from './useThumbnailDrop';

export default function ThumbnailPicker({
  thumbnail,
  onFiles,
  onRemove,
  disabled,
  busy,
  loading = false,
  compact = false,
  dropEnabled = true,
  highlighted = false,
  title,
}) {
  const input = useRef(null);
  const [url, setUrl] = useState('');
  const [urlDialogOpen, setUrlDialogOpen] = useState(false);
  useEffect(() => {
    const next = thumbnail?.blob ? URL.createObjectURL(thumbnail.blob) : '';
    setUrl(next);
    return () => {
      if (next) URL.revokeObjectURL(next);
    };
  }, [thumbnail?.blob]);
  const { dragging, dropProps } = useThumbnailDrop(
    onFiles,
    disabled || busy || loading,
  );
  const active = dragging || highlighted;
  const label = `${thumbnail ? 'Replace' : 'Add'} thumbnail${title ? ` for ${title}` : ''}`;
  const preview = (
    <ButtonBase
      onClick={(event) => {
        event.stopPropagation();
        input.current?.click();
      }}
      disabled={disabled || busy || loading}
      aria-label={label}
      sx={{
        width: compact ? 88 : { xs: '100%', sm: 160 },
        aspectRatio: '16 / 9',
        flexShrink: 0,
        borderRadius: 1,
        overflow: 'hidden',
        position: 'relative',
        bgcolor: active ? '#e6eafd' : '#f3f5fd',
        color: '#5568d3',
        border: `1px ${thumbnail ? 'solid' : 'dashed'} ${active ? '#5568d3' : '#c3cbea'}`,
        outline: active ? '2px solid #667eea' : undefined,
        outlineOffset: 2,
        '&.Mui-focusVisible': {
          outline: '2px solid #24304f',
          outlineOffset: 3,
        },
        '&:hover .thumbnail-prompt': { opacity: 1 },
      }}
    >
      {url && (
        <Box
          component="img"
          src={url}
          alt={`Thumbnail for ${title || 'this video'}`}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            position: 'absolute',
          }}
        />
      )}
      {busy || loading ? (
        <CircularProgress
          size={20}
          aria-label={busy ? 'Saving thumbnail' : 'Loading thumbnail'}
        />
      ) : (
        <Stack
          className="thumbnail-prompt"
          alignItems="center"
          justifyContent="center"
          sx={{
            position: 'absolute',
            inset: 0,
            opacity: !url || active ? 1 : 0,
            bgcolor: url ? 'rgba(36,48,79,0.82)' : 'transparent',
            color: url ? 'white' : 'inherit',
          }}
        >
          <AddPhotoAlternateOutlined sx={{ fontSize: compact ? 20 : 26 }} />
          <Typography
            variant="caption"
            sx={{ fontSize: compact ? 10 : 12, lineHeight: 1.4 }}
          >
            {active ? 'Drop image' : url ? 'Replace' : 'Add image'}
          </Typography>
        </Stack>
      )}
    </ButtonBase>
  );
  return (
    <Box
      {...(dropEnabled ? dropProps : {})}
      onClick={(event) => event.stopPropagation()}
      sx={{ minWidth: 0 }}
    >
      <input
        ref={input}
        type="file"
        accept="image/jpeg,image/png"
        hidden
        aria-label={label}
        onChange={(event) => {
          const files = Array.from(event.target.files || []);
          event.target.value = '';
          if (files.length) onFiles(files);
        }}
      />
      {compact ? (
        <Tooltip
          title={
            disabled
              ? 'Loading thumbnail…'
              : 'Drop an image on this video, or click to choose'
          }
        >
          <Box component="span" sx={{ display: 'inline-flex' }}>
            {preview}
          </Box>
        </Tooltip>
      ) : (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', sm: 'center' }}
        >
          {preview}
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}
            >
              {thumbnail?.fileName || 'Drop a cover image here'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              JPG or PNG, up to 100 MB. Images over 2 MB are compressed. A 16:9 image works best.
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <Button
                size="small"
                variant="outlined"
                disabled={disabled || busy || loading}
                onClick={() => input.current?.click()}
              >
                {thumbnail ? 'Replace image' : 'Choose image'}
              </Button>
              <Button
                size="small"
                variant="outlined"
                disabled={disabled || busy || loading}
                onClick={() => setUrlDialogOpen(true)}
              >
                Use URL
              </Button>
              {thumbnail && (
                <Button
                  size="small"
                  disabled={disabled || busy || loading}
                  onClick={onRemove}
                >
                  Remove
                </Button>
              )}
            </Stack>
          </Box>
          <ThumbnailUrlDialog
            open={urlDialogOpen}
            onClose={() => setUrlDialogOpen(false)}
            onImage={(file) => onFiles([file])}
          />
        </Stack>
      )}
    </Box>
  );
}
